import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import { useCallbackObservable, useObservable } from 'react-use-observable';

import { LoadingOutlined } from '@ant-design/icons';

import { FINANCIAL_BLOCK_TOOLTIP_MESSAGE } from 'helpers/constants';
import { HotjarEvents, submitEvent } from 'hotjar';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { BANK_LIST } from 'v2/helpers/constants';
import type { ISendTransfer, SendTransferInvoice } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import financialService from 'v2/services/financial';
import { npsService } from 'v2/services/nps';

import AnticipateStep from './AnticipateStep';
import type { TransferByInvoice } from '../constants';
import SuccessStep from './SuccessStep/index';
import TransferStep from './TransferStep';
import { MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL } from './TransferStep/contants';

type Props = {
  isOpen: boolean;
  showAnticipateStep: boolean;
  setClose: () => void;
  handleUpdateAvailableAmount: () => void;
  handleToggleAnticipateModal: () => void;
};

const TRANSFER_FEEDBACK_SURVEY_ID = 796843;
const TRANSFER_FEEDBACK_SURVEY_EXPIRATION_IN_DAYS = 90;

const setHotjarTransferSurvey = async () => {
  const lastAnswer = await npsService.getSurveyLastAnswer(TRANSFER_FEEDBACK_SURVEY_ID);

  if (lastAnswer) {
    const isSurveyExpired = lastAnswer.answered && lastAnswer.days_count >= TRANSFER_FEEDBACK_SURVEY_EXPIRATION_IN_DAYS;
    const shouldShowSurvey = !lastAnswer.answered || isSurveyExpired;

    if (shouldShowSurvey) submitEvent(HotjarEvents.TRANSFER_REQUEST_MADE);
  }
};

function TransferModal({ isOpen, setClose, handleToggleAnticipateModal, handleUpdateAvailableAmount }: Props) {
  const [accountBankList, setAccountBankList] = useState(null);
  const [selectedBank, setSelectedBank] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formIsValid, setFormIsValid] = useState(true);
  const [willAnticipateNow, setWillAnticipateNow] = useState(false);
  const [valueToTransfer, setValueToTransfer] = useState(null);
  const [transferByInvoiceObj, setTransferByInvoiceObj] = useState<TransferByInvoice>();

  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);
  const [betaModules] = useObservable(() => eduzzAccountsService.getBetaModules(), []);
  const [bankAccounts] = useObservable(() => financialService.listBankAccounts({ active: 'true' }), []);

  const userHasBlockTransferBetaModule = betaModules?.includes('block_transfer');
  const userHasTransferByInvoiceBetaModule = betaModules?.includes('transferencia_fatura');
  const validationMinValue = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;

  const isValidTransferByInvoice = transferByInvoiceObj?.approx_amount > validationMinValue;

  const [sendTransfer] = useCallbackObservable((data: ISendTransfer | SendTransferInvoice) => {
    setLoading(true);

    const onResult = tap(
      () => {
        toast.success('Sucesso, transferência enviada para processamento com êxito!');
        handleUpdateAvailableAmount();
        setLoading(false);
        setShowSuccess(true);
        setHotjarTransferSurvey();
      },
      err => {
        toast.error(err);
        setLoading(false);
      }
    );

    if ('transfer_key' in data && data.transfer_key) {
      return financialService.sendTransferByInvoice(data).pipe(onResult);
    }

    return financialService.sendTransfer(data as ISendTransfer).pipe(onResult);
  }, []);

  const disableTransferButton = useMemo(() => {
    if (!willAnticipateNow && (!formIsValid || !selectedBank || loading)) {
      return true;
    }

    return false;
  }, [formIsValid, selectedBank, loading, willAnticipateNow]);

  const verifyTransferByInvoice = async (amount: number) => {
    try {
      setLoading(true);
      const { data } = await financialService.verifyTransferByInvoice(amount);
      setTransferByInvoiceObj(data);
    } catch (error) {
      toast.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoToAnticipate = () => {
    handleToggleAnticipateModal();
    setClose();
  };

  const handleResetTransferByInvoice = () => setTransferByInvoiceObj(null);

  const blockedTransferBetaModule = useMemo(() => {
    if (userHasBlockTransferBetaModule) {
      return true;
    }

    return false;
  }, [userHasBlockTransferBetaModule]);

  const getCurrentCtaText = () => {
    if (loading) return <LoadingOutlined style={{ fontSize: 12 }} />;
    if (willAnticipateNow) return 'Quero fazer a antecipação';
    if (showSuccess) return 'OK';
    if (userHasTransferByInvoiceBetaModule && !isValidTransferByInvoice) return 'Verificar';
    return 'Transferir valor';
  };

  const getCurrentProceedClick = () => {
    if (willAnticipateNow) return handleGoToAnticipate;
    if (showSuccess) return setClose;
    if (userHasTransferByInvoiceBetaModule && !isValidTransferByInvoice)
      return () => verifyTransferByInvoice(valueToTransfer);

    if (userHasTransferByInvoiceBetaModule && isValidTransferByInvoice) {
      return () => sendTransfer({ transfer_method_id: selectedBank, transfer_key: transferByInvoiceObj.transfer_key });
    }

    return () => sendTransfer({ transfer_method_id: selectedBank, value: valueToTransfer });
  };

  const getCurrentStep = () => {
    if (willAnticipateNow) return <AnticipateStep />;
    if (showSuccess) return <SuccessStep />;
    return (
      <TransferStep
        loading={loading}
        formIsValid={formIsValid}
        selectedBank={selectedBank}
        setSelectedBank={setSelectedBank}
        valueToTransfer={valueToTransfer}
        setValueToTransfer={setValueToTransfer}
        accountBankList={accountBankList}
        setFormIsValid={setFormIsValid}
        transferByInvoiceObj={transferByInvoiceObj}
        resetTransferByInvoiceObj={handleResetTransferByInvoice}
      />
    );
  };

  const handleClose = () => (willAnticipateNow ? setWillAnticipateNow(false) : setClose());

  useEffect(() => {
    if (!accountBankList && bankAccounts?.length) {
      setAccountBankList(
        bankAccounts.map(bankAccount => {
          const { id, bank_id, agency, agency_digit, account, account_digit } = bankAccount;
          return {
            label: `${bank_id} - ${BANK_LIST[bank_id as keyof typeof BANK_LIST]} - Ag: ${agency}-${agency_digit} - Conta ${account}-${account_digit}`,
            value: id
          };
        })
      );
      setSelectedBank(bankAccounts[0].id);
    }
  }, [accountBankList, bankAccounts]);

  return (
    <Modal
      open={isOpen}
      size='sm'
      setClose={handleClose}
      textTitle={!showSuccess ? 'Transferir saldo' : 'Parabéns!'}
      textReturnButton='Agora não'
      textProceedButton={getCurrentCtaText()}
      hideReturnButton={showSuccess}
      handleProceedClick={getCurrentProceedClick()}
      isProceedButtonDisabled={disableTransferButton || blockedTransferBetaModule}
      textTooltipProceedButton={blockedTransferBetaModule ? FINANCIAL_BLOCK_TOOLTIP_MESSAGE : ''}
      enableScroll
    >
      {getCurrentStep()}
    </Modal>
  );
}

export default TransferModal;
